import React from "react";
import classNames from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import {
    AngleSmallLeftIcon,
    AngleSmallRightIcon,
    StyledIconButton,
} from "@aureskonnect/react-ui";

import { designStore, setSelectedLanguage } from "@store";

import { capitalize } from "@helpers/general";

import "../index.css";
import "swiper/swiper-bundle.css";

const LANGUAGE_BUTTON_TESTID_PREFIX = "cgt_btn_";

// En dessous de ce seuil, les langues sont rendues en ligne ; au-delà, on bascule
// sur le carousel Swiper (navigation prev/next).
const MAX_LANGUAGES_WITHOUT_SWIPER = 5;

// Touches qui valident une sélection au clavier (a11y : un div role=button doit
// réagir à Entrée et Espace comme un vrai bouton — WCAG 2.1.1).
const SELECT_KEYS = ["Enter", " "];

function getLanguageButtonTestId(languageName: string): string {
    // On conserve lettres (accents inclus, ex. ç dans "français") et chiffres ;
    // seuls les séparateurs (espaces, parenthèses…) deviennent "_". Le flag `u`
    // active les classes Unicode \p{L}/\p{N}.
    const sanitizedName = languageName.replace(/[^\p{L}\p{N}]/gu, "_");
    return `${LANGUAGE_BUTTON_TESTID_PREFIX}${sanitizedName}`;
}

function handleLanguageKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    onSelect: () => void
): void {
    if (SELECT_KEYS.includes(event.key)) {
        event.preventDefault();
        onSelect();
    }
}

export function SwiperLanguagesWrapper(): JSX.Element {
    const { t } = useTranslation();
    const { selectedLanguage, activePageName, activeLanguages } = useSnapshot(
        designStore
    );

    const navigationPrevRef = React.useRef(null);
    const navigationNextRef = React.useRef(null);

    return (
        <div>
            {activeLanguages.length < MAX_LANGUAGES_WITHOUT_SWIPER ? (
                <div
                    className="d-flex flex-row justify-content-center align-items-center"
                    style={{
                        width: "95%",
                        height: "36px !important",
                        gap: "10px",
                        marginLeft: "20px",
                        marginBottom: "2px",
                        marginTop: "2px",
                    }}
                >
                    {activeLanguages.map((language) => {
                        return (
                            <div
                                key={language.name}
                                role="button"
                                tabIndex={0}
                                aria-label={capitalize(language.title)}
                                className={classNames(
                                    "width__clz border border-white rounded",
                                    getLanguageButtonTestId(language.title),
                                    {
                                        "slide-color__clz":
                                            language.name === selectedLanguage,
                                    }
                                )}
                                style={{
                                    width: "100%",
                                }}
                                onClick={() => {
                                    setSelectedLanguage(language.name);
                                }}
                                onKeyDown={(event) =>
                                    handleLanguageKeyDown(event, () =>
                                        setSelectedLanguage(language.name)
                                    )
                                }
                            >
                                {capitalize(language.title)}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ width: "95%", marginLeft: "13px" }}
                >
                    <StyledIconButton
                        ref={navigationPrevRef}
                        aria-label={t("Previous")}
                        className="m-0 p-0 swiper-button__clz"
                    >
                        <AngleSmallLeftIcon
                            height={50}
                            width={40}
                            fill="#323332"
                        />
                    </StyledIconButton>
                    <Swiper
                        className="design-ways-swiper-wrapper__clz"
                        modules={[Navigation]}
                        navigation={{
                            prevEl: navigationPrevRef.current,
                            nextEl: navigationNextRef.current,
                        }}
                        spaceBetween={14}
                        slidesPerView={4}
                    >
                        {activeLanguages.map((item) => {
                            return (
                                <SwiperSlide
                                    role="button"
                                    tabIndex={0}
                                    aria-label={capitalize(item.title)}
                                    className={classNames(
                                        "width__clz border border-white rounded",
                                        getLanguageButtonTestId(item.title),
                                        {
                                            "languages-swiper-slide-width__clz":
                                                activePageName !== "payment",
                                            "languages-payment-swiper-slide-width__clz":
                                                activePageName === "payment",
                                            "slide-color__clz":
                                                selectedLanguage === item.name,
                                        }
                                    )}
                                    key={item.name}
                                    onClick={() => {
                                        setSelectedLanguage(item.name);
                                    }}
                                    onKeyDown={(event) =>
                                        handleLanguageKeyDown(event, () =>
                                            setSelectedLanguage(item.name)
                                        )
                                    }
                                >
                                    <span
                                        style={{
                                            cursor: "pointer",
                                        }}
                                    >
                                        {capitalize(item.title)}
                                    </span>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                    <StyledIconButton
                        ref={navigationNextRef}
                        aria-label={t("Next")}
                        className="m-0 p-0 swiper-button__clz"
                    >
                        <AngleSmallRightIcon
                            height={50}
                            width={40}
                            fill="#323332"
                        />
                    </StyledIconButton>
                </div>
            )}
        </div>
    );
}
